/**
 * LinkedDeviceProvisioningSelfTest.ts
 *
 * Self-test suite for the linked-device QR provisioning module.
 * Registers with SelfTestRunner at module load time; auto-discovered via
 * Vite's import.meta.glob pattern `../**\/*SelfTest.ts`.
 *
 * Tests cover:
 *  1.  QR codec round-trip
 *  2.  QR URL prefix validation
 *  3.  QR payload length validation
 *  4.  Sealed round-trip (seal → open recovers bundle)
 *  5.  Wrong session-id in QR (AAD mismatch)
 *  6.  Wrong ephemeral private key (GCM auth-tag failure)
 *  7.  Tampered ciphertext (byte flip → auth-tag failure)
 *  8.  Replay across sessions (same sealed bundle, different QR)
 *  9.  Bundle deserialisation: expected fields present
 *  10. Bundle deserialisation: missing field rejects cleanly
 */

import {
  SelfTestRunner,
  buildSuiteResult,
  fail,
  pass,
} from "../selftest";
import type { TestCase } from "../selftest";
import {
  encodeQR,
  decodeQR,
  generateProvisioningQR,
  generateEphemeralKeyPair,
  sealProvisioning,
  openProvisioning,
} from "./LinkedDeviceProvisioning";
import type { ProvisioningBundle, ProvisioningQR } from "./LinkedDeviceProvisioning";

// ---------------------------------------------------------------------------
// Test-fixture helpers
// ---------------------------------------------------------------------------

/** A fully-populated ProvisioningBundle for tests. */
function makeBundle(overrides: Partial<ProvisioningBundle> = {}): ProvisioningBundle {
  return {
    identityPublicKey: btoa("identityPublicKey_32bytes_padding"),
    identityPrivateKey: btoa("identityPrivateKey32bytes_padding"),
    signedPreKeyId: 1,
    signedPreKeyPublic: btoa("signedPreKeyPublic_32bytesPadding"),
    signedPreKeyPrivate: btoa("signedPreKeyPrivate32bytesPadding"),
    signedPreKeySignature: btoa("signedPreKeySig_64bytesPadding_padding_padding_pad"),
    registrationId: 12345,
    deviceId: 1,
    onboardingVersion: 1,
    ...overrides,
  };
}

/** Compare two Uint8Arrays for value equality. */
function bytesEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

/** Compare two ProvisioningQR structs for deep equality. */
function qrEqual(a: ProvisioningQR, b: ProvisioningQR): boolean {
  return (
    bytesEqual(a.sessionId, b.sessionId) &&
    bytesEqual(a.ephemeralPub, b.ephemeralPub) &&
    bytesEqual(a.nonce, b.nonce)
  );
}

/** Compare two ProvisioningBundle structs for equality. */
function bundleEqual(a: ProvisioningBundle, b: ProvisioningBundle): boolean {
  return (
    a.identityPublicKey === b.identityPublicKey &&
    a.identityPrivateKey === b.identityPrivateKey &&
    a.signedPreKeyId === b.signedPreKeyId &&
    a.signedPreKeyPublic === b.signedPreKeyPublic &&
    a.signedPreKeyPrivate === b.signedPreKeyPrivate &&
    a.signedPreKeySignature === b.signedPreKeySignature &&
    a.registrationId === b.registrationId &&
    a.deviceId === b.deviceId &&
    a.onboardingVersion === b.onboardingVersion
  );
}

// ---------------------------------------------------------------------------
// Individual test implementations
// ---------------------------------------------------------------------------

/**
 * TC-1: QR codec round-trip.
 * encode(qr) → decode(url) must produce a struct with the same bytes.
 */
async function testQRCodecRoundTrip(): Promise<TestCase> {
  try {
    const { qr } = await generateProvisioningQR();
    const url = encodeQR(qr);
    const recovered = decodeQR(url);
    if (!qrEqual(qr, recovered)) {
      return fail(
        "QR codec round-trip",
        "decoded QR fields do not match original",
      );
    }
    return pass("QR codec round-trip");
  } catch (e) {
    return fail("QR codec round-trip", String(e));
  }
}

/**
 * TC-2: QR URL prefix validation.
 * A URL not starting with "kaether:link:v1:" must be rejected.
 */
async function testQRPrefixValidation(): Promise<TestCase> {
  const badUrls = [
    "https://example.com/link/abc",
    "kaether:link:v2:abc",
    "kaether:link:abc",
    "",
  ];
  for (const url of badUrls) {
    try {
      decodeQR(url);
      return fail(
        "QR URL prefix validation",
        `expected error for bad URL "${url}" but none was thrown`,
      );
    } catch {
      // expected
    }
  }
  return pass("QR URL prefix validation");
}

/**
 * TC-3: QR payload length validation.
 * A valid prefix but wrong-length payload must be rejected.
 */
async function testQRLengthValidation(): Promise<TestCase> {
  // Encode a 10-byte payload instead of 60 bytes.
  const shortPayload = btoa("0123456789")
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
  const badUrl = "kaether:link:v1:" + shortPayload;
  try {
    decodeQR(badUrl);
    return fail(
      "QR length validation",
      "expected error for short payload but none was thrown",
    );
  } catch {
    return pass("QR length validation");
  }
}

/**
 * TC-4: Sealed round-trip.
 * seal(bundle, qr, existingEph) → open(sealed, newEphPriv, qr) recovers bundle.
 */
async function testSealedRoundTrip(): Promise<TestCase> {
  try {
    const { qr, ephemeralPrivKey: newDeviceEphPriv } = await generateProvisioningQR();
    const { privKey: existingEphPriv, pubKey: existingEphPub } =
      await generateEphemeralKeyPair();
    const bundle = makeBundle();

    const sealed = await sealProvisioning(bundle, qr, existingEphPriv, existingEphPub);
    const recovered = await openProvisioning(sealed, newDeviceEphPriv, qr);

    if (!bundleEqual(bundle, recovered)) {
      return fail("Sealed round-trip", "recovered bundle does not match original");
    }
    return pass("Sealed round-trip");
  } catch (e) {
    return fail("Sealed round-trip", String(e));
  }
}

/**
 * TC-5: Wrong session-id in QR.
 * Opening with a QR that has a different sessionId mutates the AAD, so
 * openProvisioning should throw before even touching AES-GCM.
 */
async function testWrongSessionId(): Promise<TestCase> {
  try {
    const { qr: originalQR, ephemeralPrivKey: newDeviceEphPriv } =
      await generateProvisioningQR();
    const { privKey: existingEphPriv, pubKey: existingEphPub } =
      await generateEphemeralKeyPair();
    const bundle = makeBundle();

    const sealed = await sealProvisioning(
      bundle,
      originalQR,
      existingEphPriv,
      existingEphPub,
    );

    // Build a QR with the same ephemeralPub + nonce but a different sessionId.
    const differentSessionId = crypto.getRandomValues(new Uint8Array(16));
    const tamperedQR: ProvisioningQR = {
      ...originalQR,
      sessionId: differentSessionId,
    };

    try {
      await openProvisioning(sealed, newDeviceEphPriv, tamperedQR);
      return fail(
        "Wrong session-id",
        "expected error for wrong session-id but none was thrown",
      );
    } catch {
      return pass("Wrong session-id");
    }
  } catch (e) {
    return fail("Wrong session-id", `unexpected setup error: ${String(e)}`);
  }
}

/**
 * TC-6: Wrong ephemeral private key.
 * Opening with a different new-device ephemeral private key produces a
 * different shared secret → AES-GCM auth-tag failure.
 */
async function testWrongEphemeralPrivKey(): Promise<TestCase> {
  try {
    const { qr, ephemeralPrivKey: _correctPriv } = await generateProvisioningQR();
    const { privKey: existingEphPriv, pubKey: existingEphPub } =
      await generateEphemeralKeyPair();
    const bundle = makeBundle();

    const sealed = await sealProvisioning(bundle, qr, existingEphPriv, existingEphPub);

    // Use a completely different ephemeral key pair for the "new device".
    const { privKey: wrongPriv } = await generateEphemeralKeyPair();

    try {
      await openProvisioning(sealed, wrongPriv, qr);
      return fail(
        "Wrong ephemeral private key",
        "expected GCM auth-tag error but none was thrown",
      );
    } catch {
      return pass("Wrong ephemeral private key");
    }
  } catch (e) {
    return fail("Wrong ephemeral private key", `unexpected setup error: ${String(e)}`);
  }
}

/**
 * TC-7: Tampered ciphertext.
 * Flipping a byte in the ciphertext (before the tag) must trigger GCM
 * authentication failure.
 */
async function testTamperedCiphertext(): Promise<TestCase> {
  try {
    const { qr, ephemeralPrivKey: newDeviceEphPriv } = await generateProvisioningQR();
    const { privKey: existingEphPriv, pubKey: existingEphPub } =
      await generateEphemeralKeyPair();
    const bundle = makeBundle();

    const sealed = await sealProvisioning(bundle, qr, existingEphPriv, existingEphPub);

    // Flip the first byte of the ciphertext.
    const tampered = new Uint8Array(sealed.ciphertext);
    tampered[0] ^= 0xff;

    const tamperedSealed = { ...sealed, ciphertext: tampered };

    try {
      await openProvisioning(tamperedSealed, newDeviceEphPriv, qr);
      return fail(
        "Tampered ciphertext",
        "expected GCM auth-tag error for tampered ciphertext but none was thrown",
      );
    } catch {
      return pass("Tampered ciphertext");
    }
  } catch (e) {
    return fail("Tampered ciphertext", `unexpected setup error: ${String(e)}`);
  }
}

/**
 * TC-8: Replay across sessions.
 * A sealed bundle from session A cannot be opened against a freshly-generated
 * QR (session B) because the AAD includes the session-id.
 */
async function testReplayAcrossSessions(): Promise<TestCase> {
  try {
    // Session A: seal a bundle.
    const { qr: qrA, ephemeralPrivKey: _newPrivA } = await generateProvisioningQR();
    const { privKey: existingPrivA, pubKey: existingPubA } =
      await generateEphemeralKeyPair();
    const bundle = makeBundle();
    const sealedA = await sealProvisioning(bundle, qrA, existingPrivA, existingPubA);

    // Session B: a completely fresh provisioning QR on a different new device.
    const { qr: qrB, ephemeralPrivKey: newPrivB } = await generateProvisioningQR();

    // Try to open session A's sealed bundle using session B's QR + private key.
    // The AAD in sealedA encodes qrA's sessionId and ephemeralPub; qrB has
    // different values → AAD mismatch before AES-GCM is even attempted.
    try {
      await openProvisioning(sealedA, newPrivB, qrB);
      return fail(
        "Replay across sessions",
        "expected AAD mismatch error for cross-session replay but none was thrown",
      );
    } catch {
      return pass("Replay across sessions");
    }
  } catch (e) {
    return fail("Replay across sessions", `unexpected setup error: ${String(e)}`);
  }
}

/**
 * TC-9: Bundle deserialisation — expected fields present.
 * After a successful open, the recovered bundle has all required fields
 * with their original values.
 */
async function testBundleDeserialisationFields(): Promise<TestCase> {
  try {
    const { qr, ephemeralPrivKey: newDeviceEphPriv } = await generateProvisioningQR();
    const { privKey: existingEphPriv, pubKey: existingEphPub } =
      await generateEphemeralKeyPair();
    const bundle = makeBundle({
      signedPreKeyId: 42,
      registrationId: 99999,
      deviceId: 3,
    });

    const sealed = await sealProvisioning(bundle, qr, existingEphPriv, existingEphPub);
    const recovered = await openProvisioning(sealed, newDeviceEphPriv, qr);

    // Spot-check key fields.
    if (recovered.signedPreKeyId !== 42) {
      return fail(
        "Bundle fields",
        `signedPreKeyId: expected 42, got ${String(recovered.signedPreKeyId)}`,
      );
    }
    if (recovered.registrationId !== 99999) {
      return fail(
        "Bundle fields",
        `registrationId: expected 99999, got ${String(recovered.registrationId)}`,
      );
    }
    if (recovered.onboardingVersion !== 1) {
      return fail(
        "Bundle fields",
        `onboardingVersion: expected 1, got ${String(recovered.onboardingVersion)}`,
      );
    }
    return pass("Bundle deserialisation: fields");
  } catch (e) {
    return fail("Bundle deserialisation: fields", String(e));
  }
}

/**
 * TC-10: Bundle deserialisation — missing field triggers a clear error.
 * A bundle JSON that lacks a required field must throw with a descriptive
 * message rather than silently producing a partial object.
 */
async function testBundleDeserialisationMissingField(): Promise<TestCase> {
  try {
    const { qr, ephemeralPrivKey: newDeviceEphPriv } = await generateProvisioningQR();
    const { privKey: existingEphPriv, pubKey: existingEphPub } =
      await generateEphemeralKeyPair();

    // Build a bundle missing `identityPrivateKey`.
    const incompleteBundle = {
      identityPublicKey: btoa("pub"),
      // identityPrivateKey intentionally omitted
      signedPreKeyId: 1,
      signedPreKeyPublic: btoa("spkPub"),
      signedPreKeyPrivate: btoa("spkPriv"),
      signedPreKeySignature: btoa("spkSig"),
      registrationId: 1,
      deviceId: 1,
      onboardingVersion: 1,
    };

    // Manually construct a sealed bundle wrapping the incomplete JSON.
    const dhSecret = await (async () => {
      const pkcs8Header = new Uint8Array([
        0x30, 0x2e, 0x02, 0x01, 0x00, 0x30, 0x05,
        0x06, 0x03, 0x2b, 0x65, 0x6e,
        0x04, 0x22, 0x04, 0x20,
      ]);
      const pkcs8 = new Uint8Array(pkcs8Header.length + existingEphPriv.length);
      pkcs8.set(pkcs8Header, 0);
      pkcs8.set(existingEphPriv, pkcs8Header.length);
      const privKey = await crypto.subtle.importKey("pkcs8", pkcs8, { name: "X25519" }, false, ["deriveBits"]);
      const pubKey = await crypto.subtle.importKey("raw", qr.ephemeralPub, { name: "X25519" }, false, []);
      const bits = await crypto.subtle.deriveBits({ name: "X25519", public: pubKey }, privKey, 256);
      return new Uint8Array(bits);
    })();

    const ikmKey = await crypto.subtle.importKey("raw", dhSecret, { name: "HKDF" }, false, ["deriveKey"]);
    const wrappingKey = await crypto.subtle.deriveKey(
      { name: "HKDF", hash: "SHA-256", salt: new Uint8Array(32), info: new TextEncoder().encode("kaether:linked-device:v1") },
      ikmKey,
      { name: "AES-GCM", length: 256 },
      false,
      ["encrypt"],
    );
    const plaintext = new TextEncoder().encode(JSON.stringify(incompleteBundle));
    const aad = new Uint8Array(16 + 32);
    aad.set(qr.sessionId, 0);
    aad.set(qr.ephemeralPub, 16);
    const ciphertextBuf = await crypto.subtle.encrypt(
      { name: "AES-GCM", iv: qr.nonce, additionalData: aad },
      wrappingKey,
      plaintext,
    );

    const sealed = {
      ciphertext: new Uint8Array(ciphertextBuf),
      aad,
      existingDeviceEphPub: existingEphPub,
    };

    try {
      await openProvisioning(sealed, newDeviceEphPriv, qr);
      return fail(
        "Bundle deserialisation: missing field",
        "expected validation error for missing identityPrivateKey but none was thrown",
      );
    } catch (e) {
      const msg = String(e);
      if (!msg.includes("identityPrivateKey")) {
        return fail(
          "Bundle deserialisation: missing field",
          `error thrown but message does not name the missing field: "${msg}"`,
        );
      }
      return pass("Bundle deserialisation: missing field");
    }
  } catch (e) {
    return fail(
      "Bundle deserialisation: missing field",
      `unexpected setup error: ${String(e)}`,
    );
  }
}

// ---------------------------------------------------------------------------
// Suite registration
// ---------------------------------------------------------------------------

SelfTestRunner.register({
  name: "Linked-device QR provisioning",
  isVerified: true,
  run: async () => {
    const cases: TestCase[] = await Promise.all([
      testQRCodecRoundTrip(),
      testQRPrefixValidation(),
      testQRLengthValidation(),
      testSealedRoundTrip(),
      testWrongSessionId(),
      testWrongEphemeralPrivKey(),
      testTamperedCiphertext(),
      testReplayAcrossSessions(),
      testBundleDeserialisationFields(),
      testBundleDeserialisationMissingField(),
    ]);
    return buildSuiteResult(cases);
  },
});
